import React, { useState, useMemo } from "react";
import { GameState, PlayerState } from "../../../core/protocol/types";
import ResultScreenLayout from "./ResultScreenLayout";
import ResultHeader from "./ResultHeader";
import ResultTabs from "./ResultTabs";
import ResultFooter from "./ResultFooter";
import RankTable from "./tables/RankTable";
import SpeedTable from "./tables/SpeedTable";
import HighScoreTable from "./tables/HighScoreTable";
import "../ResultScreen.css";

type SortColumn = "frags" | "deaths" | "score" | "lines";
type PageType = "rank" | "speed" | "highscore";

interface TeamStats {
  frags: number;
  deaths: number;
  score: number;
  lines: number;
  players: number;
  roundWins?: number;
}

interface SinglePlayerResultScreenProps {
  gameState: GameState;
  localStats: { score: number; level: number; lines: number };
  onQuit: () => void;
  onRestart?: () => void;
  gameStats?: Record<string, { getValue: () => number }>;
  highScoreRank?: number;
}

const SinglePlayerResultScreen: React.FC<SinglePlayerResultScreenProps> = ({
  gameState,
  localStats,
  onQuit,
  onRestart,
  gameStats,
  highScoreRank = -1,
}) => {
  const [activeSort, setActiveSort] = useState<SortColumn>("score");
  const [activePage, setActivePage] = useState<PageType>("rank");

  // Calculate team stats (single player is on team 0)
  const teamStats = useMemo(() => {
    const stats = new Map<number, TeamStats>();

    // Initialize all teams
    for (let i = 0; i < 8; i++) {
      stats.set(i, {
        frags: 0,
        deaths: 0,
        score: 0,
        lines: 0,
        players: 0,
        roundWins: 0,
      });
    }

    // Aggregate stats by team
    gameState.players.forEach((player) => {
      if (player.isDedicatedServer) return;
      const team = player.team ?? 0;
      const teamStat = stats.get(team);
      if (teamStat) {
        teamStat.frags += player.frags || 0;
        teamStat.deaths += player.deaths || 0;
        teamStat.score += player.score || 0;
        teamStat.lines += player.lines || 0;
        teamStat.roundWins += player.roundWins || 0;
        teamStat.players += 1;
      }
    });

    return stats;
  }, [gameState.players]);

  // Get all players
  const allPlayers = useMemo(() => {
    const players: PlayerState[] = [];
    gameState.players.forEach((player) => {
      if (player.isDedicatedServer) return;
      players.push(player);
    });
    return players;
  }, [gameState.players]);

  // Sort ALL players globally by active sort column
  const sortedPlayers = useMemo(() => {
    const sorted = [...allPlayers];
    sorted.sort((a, b) => {
      const aVal = a[activeSort] || 0;
      const bVal = b[activeSort] || 0;
      // For deaths, lower is better, so reverse sort
      if (activeSort === "deaths") {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
    return sorted;
  }, [allPlayers, activeSort]);

  // Sort teams (only team 0 for single player)
  const activeTeams = useMemo(() => {
    const teams = Array.from(teamStats.entries())
      .filter(([_, stats]) => stats.players > 0)
      .sort(([teamIdA, statsA], [teamIdB, statsB]) => {
        // Compare by active sort
        const aVal = statsA[activeSort] || 0;
        const bVal = statsB[activeSort] || 0;
        if (activeSort === "deaths") {
          return aVal - bVal; // Lower deaths is better
        }
        return bVal - aVal; // Higher is better
      });
    return teams;
  }, [teamStats, activeSort]);

  // Group sorted players by team
  const playersByTeam = useMemo(() => {
    const byTeam = new Map<number, PlayerState[]>();
    sortedPlayers.forEach((player) => {
      const team = player.team ?? 0;
      if (!byTeam.has(team)) {
        byTeam.set(team, []);
      }
      byTeam.get(team)!.push(player);
    });
    return byTeam;
  }, [sortedPlayers]);

  const localPlayer = gameState.localConnectionId
    ? gameState.connections.get(gameState.localConnectionId)
    : null;
  const localTeam = localPlayer?.team ?? 0;
  const localNumPlayer = localPlayer?.numPlayer;

  const handleSort = (column: SortColumn) => {
    setActiveSort(column);
  };

  const handlePageChange = (page: string) => {
    if (page === "rank" || page === "speed" || page === "highscore") {
      setActivePage(page);
    }
  };

  return (
    <ResultScreenLayout>
      <ResultHeader winningTeam={null} isSinglePlayer={true} />
      <ResultTabs
        activePage={activePage}
        onPageChange={handlePageChange}
        isSinglePlayer={true}
        showHighScores={highScoreRank >= 0}
      />
      <div className="result-screen-content">
        {activePage === "rank" && (
          <RankTable
            players={sortedPlayers}
            playersByTeam={playersByTeam}
            activeTeams={activeTeams}
            activeSort={activeSort}
            onSort={handleSort}
            localConnectionId={gameState.localConnectionId}
            localNumPlayer={localNumPlayer}
            localTeam={localTeam}
            isSinglePlayer={true}
          />
        )}
        {activePage === "speed" && (
          <SpeedTable
            playersByTeam={playersByTeam}
            activeTeams={activeTeams}
            localConnectionId={gameState.localConnectionId}
            localNumPlayer={localNumPlayer}
            localTeam={localTeam}
            isSinglePlayer={true}
            gameStats={gameStats}
          />
        )}
        {activePage === "highscore" && (
          <HighScoreTable highScoreRank={highScoreRank} />
        )}
      </div>
      <ResultFooter
        onQuit={onQuit}
        onRestart={onRestart}
        isSinglePlayer={true}
      />
    </ResultScreenLayout>
  );
};

export default SinglePlayerResultScreen;

